// Option sources for the Tags filter + form: GLOBAL programme resources
// (programmes / programme_grades / programme_subjects — superadmin scope),
// unlike the org-scoped variants in pages/groups/useGroupOptions. Small
// per-programme lists loaded once and filtered client-side (v3
// `searchApi={false}` parity).
import { CrudFilter, useList } from '@refinedev/core';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  APMultiSelectOption,
  APMultiSelectOptionGroup,
  APSelectOption,
} from '@/components/ap/form';

type JsonApiRow = { id?: string | number; attributes?: Record<string, any> };

const attrs = (row: JsonApiRow) => row?.attributes ?? (row as Record<string, any>) ?? {};

const toOptions = (rows: JsonApiRow[] | undefined): APMultiSelectOption[] =>
  (rows ?? []).map((row) => {
    const item = attrs(row);
    return {
      value: item.id ?? (row.id as string | number),
      label: String(item.name ?? item.label ?? item.id),
    };
  });

/** Grades of a global programme (v3 useProgrammeGradeSelect parity). */
export const useProgrammeGradeOptions = (programmeId: string | number | undefined | null) => {
  const { data, isLoading } = useList({
    resource: 'programme_grades',
    filters: [
      { field: 'should_serialize', operator: 'eq', value: false },
      { field: 'by_programme_id', operator: 'eq', value: programmeId },
    ],
    pagination: { pageSize: 25 },
    queryOptions: { enabled: !!programmeId },
  });
  return { options: useMemo(() => toOptions(data?.data), [data?.data]), isLoading };
};

/** Subject GROUPS of a global programme (v3 useProgrammeSubjectSelect with
 *  by_type_c=group). Serialized fetch — NO should_serialize=false: the raw-row
 *  path skips ProgrammeSubjectSerializer, which is what nests unarchived child
 *  subjects as `children_attributes` (verified against the live controller).
 *  `groups`: v3 OptGroup semantics — group name = heading, child subjects
 *  selectable (value = CHILD programme_subject id); a group with no children
 *  is itself a plain selectable option. `options`: legacy flat group rows
 *  (value = GROUP id) for surfaces where v3 sent group ids
 *  (showSubjectGroupsWithoutChildren). */
export const useProgrammeSubjectGroupOptions = (
  programmeId: string | number | undefined | null,
  { unarchived = false, pageSize = 15 }: { unarchived?: boolean; pageSize?: number } = {}
) => {
  const filters: CrudFilter[] = [
    { field: 'by_programme_id', operator: 'eq', value: programmeId },
    { field: 'by_type_c', operator: 'eq', value: 'group' },
    ...(unarchived ? [{ field: 'unarchived', operator: 'eq', value: true } as CrudFilter] : []),
  ];
  const { data, isLoading } = useList({
    resource: 'programme_subjects',
    filters,
    pagination: { pageSize },
    queryOptions: { enabled: !!programmeId },
  });
  const options = useMemo<APMultiSelectOption[]>(
    () =>
      (data?.data ?? []).map((row: JsonApiRow) => {
        const item = attrs(row);
        return {
          value: item.id ?? (row.id as string | number),
          label: String(item.label ?? item.name ?? item.id),
        };
      }),
    [data?.data]
  );
  const groups = useMemo<APMultiSelectOptionGroup[]>(
    () =>
      (data?.data ?? []).map((row: JsonApiRow) => {
        const item = attrs(row);
        const children = (item.children_attributes ?? []) as Array<Record<string, any>>;
        const label = String(item.name ?? item.label ?? item.id);
        if (children.length > 0) {
          return {
            label,
            options: children.map((child) => ({
              value: child.id as string | number,
              label: String(child.name ?? child.label ?? child.id),
            })),
          };
        }
        // v3 parity: a childless group is a plain selectable option (no heading).
        return { options: [{ value: item.id ?? (row.id as string | number), label }] };
      }),
    [data?.data]
  );
  return { groups, options, isLoading };
};

/** Static tag-type options (v3 TagTypeSelect parity). */
export const useTagTypeOptions = (): APSelectOption[] => {
  const { t } = useTranslation();
  return useMemo(
    () => [
      { value: 'key_concept', label: t('SelectSubject.key_concept') },
      { value: 'related_concept', label: t('SelectSubject.related_concept') },
      { value: 'global_context', label: t('SelectSubject.global_context') },
      { value: 'possible_exploration', label: t('SelectSubject.possible_exploration') },
      { value: 'atl_skill_category', label: t('SelectSubject.atl_skill_category') },
      { value: 'command_term', label: t('SelectSubject.command_term') },
      { value: 'difficulty_level', label: t('SelectSubject.difficulty_level') },
      { value: 'teaching_level', label: t('SelectSubject.teaching_level') },
      { value: 'paper_type', label: t('SelectSubject.paper_type') },
      { value: 'depth_of_knowledge', label: t('SelectSubject.depth_of_knowledge') },
      { value: 'assessment_objective', label: t('SelectSubject.assessment_objective') },
      { value: 'category', label: t('SelectSubject.category') },
    ],
    [t]
  );
};

/** v3 TagFormItems positionOptions — display "1"…"30" mapped to lexicographic
 *  position codes (backend sorts `position COLLATE "C"`). */
export const TAG_POSITION_OPTIONS: APSelectOption[] = [
  { label: '1', value: '0J' },
  { label: '2', value: '0c' },
  { label: '3', value: '0v' },
  { label: '4', value: '1' },
  { label: '5', value: '1X' },
  { label: '6', value: '1q' },
  { label: '7', value: '2' },
  { label: '8', value: '2S' },
  { label: '9', value: '2m' },
  { label: '10', value: '3' },
  { label: '11', value: '3O' },
  { label: '12', value: '3h' },
  { label: '13', value: '4' },
  { label: '14', value: '4J' },
  { label: '15', value: '4c' },
  { label: '16', value: '4v' },
  { label: '17', value: '5' },
  { label: '18', value: '5Y' },
  { label: '19', value: '5r' },
  { label: '20', value: '6' },
  { label: '21', value: '6T' },
  { label: '22', value: '6m' },
  { label: '23', value: '7' },
  { label: '24', value: '7O' },
  { label: '25', value: '7i' },
  { label: '26', value: '8' },
  { label: '27', value: '8K' },
  { label: '28', value: '8d' },
  { label: '29', value: '8w' },
  { label: '30', value: '9' },
];
